import { A11yModule } from '@angular/cdk/a11y';
import { Component, DestroyRef, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseCellRendererComponent } from '@examdojo/ag-grid';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { NumberInputComponent, TextareaInputComponent } from '@examdojo/core/input';
import { PayoutRequestService, PayoutRequestUIModel } from '@examdojo/payments';
import { EMPTY } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'exam-payment-request-extra-bonus-cell-renderer',
  template: `
    <div class="flex h-full w-full items-center gap-1">
      <span>€{{ extraBonus.toFixed(2) }}</span>

      @if (hasReason) {
        <dojo-icon [icon]="'info-circle'" [matTooltip]="bonusReason" class="cursor-help text-gray-500" />
      }

      @if (isEditable) {
        <dojo-button
          [fill]="'outline'"
          [size]="'xs'"
          [matMenuTriggerFor]="bonusMenu"
          [matTooltip]="'Click to edit bonus'"
          (click)="openBonusMenu($event)"
          #bonusMenuTrigger="matMenuTrigger"
        >
          <dojo-icon [icon]="'pencil'" />
        </dojo-button>
      }
    </div>

    <mat-menu #bonusMenu="matMenu" class="custom-mat-menu">
      <ng-template matMenuContent>
        <div
          class="flex flex-col gap-3 p-4"
          cdkTrapFocus
          [cdkTrapFocusAutoCapture]="true"
          (click)="$event.stopPropagation()"
        >
          <h3 class="text-md font-semibold">Extra Bonus</h3>
          <p class="text-sm text-gray-600">
            You can grant a one-time extra bonus to {{ params?.data?.user_name }} for this payment period.
          </p>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Bonus Amount (€)</label>
            <y42-number-input [formCtrl]="bonusForm.controls.amount" placeholder="0.00" [step]="0.01" [max]="1000" />
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium"
              >Bonus Reason
              @if (requiresReason) {
                <span class="text-red-600">*</span>
              }
            </label>
            <y42-textarea-input
              [autoResize]="true"
              [formCtrl]="bonusForm.controls.reason"
              placeholder="Enter reason for extra bonus..."
            />
            @if (bonusForm.controls.reason.invalid && bonusForm.controls.reason.touched && requiresReason) {
              <span class="text-xs text-red-600">Bonus reason is required when amount is greater than 0</span>
            }
          </div>

          <div class="flex gap-2">
            <dojo-button [size]="'s'" [fill]="'outline'" [customWidth]="80" (click)="cancelEdit()">
              Cancel
            </dojo-button>
            <dojo-button
              [size]="'s'"
              [customWidth]="80"
              [pending]="isSaving()"
              [disabled]="bonusForm.invalid"
              (click)="saveBonus()"
            >
              Save
            </dojo-button>
          </div>
        </div>
      </ng-template>
    </mat-menu>
  `,
  imports: [
    MatTooltipModule,
    MatMenuModule,
    ButtonComponent,
    IconComponent,
    NumberInputComponent,
    TextareaInputComponent,
    ReactiveFormsModule,
    A11yModule,
  ],
})
export class PaymentRequestExtraBonusCellRendererComponent extends BaseCellRendererComponent<
  number,
  PayoutRequestUIModel
> {
  constructor(
    private readonly payoutRequestService: PayoutRequestService,
    private readonly destroyRef: DestroyRef,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    super();
  }

  readonly bonusForm = new FormGroup(
    {
      amount: new FormControl<number>(0, {
        nonNullable: true,
        validators: [Validators.min(0), Validators.max(1000)],
      }),
      reason: new FormControl<string>('', { nonNullable: true }),
    },
    { validators: this.bonusValidator.bind(this) },
  );

  readonly bonusMenuTrigger = viewChild<MatMenuTrigger>('bonusMenuTrigger');

  readonly isSaving = signal(false);

  get extraBonus(): number {
    return this.params?.data?.extra_bonus_in_eur ?? 0;
  }

  get bonusReason(): string {
    return this.params?.data?.extra_bonus_reason ?? '';
  }

  get hasReason(): boolean {
    return !!this.bonusReason;
  }

  get isEditable(): boolean {
    return this.params?.data?.status === 'WAITING_FOR_APPROVAL';
  }

  get requiresReason(): boolean {
    return (this.bonusForm.controls.amount.value ?? 0) > 0;
  }

  openBonusMenu(event: Event) {
    event.stopPropagation();

    this.bonusForm.patchValue({
      amount: this.extraBonus,
      reason: this.params?.data?.extra_bonus_reason ?? '',
    });
  }

  cancelEdit() {
    this.isSaving.set(false);
    this.bonusForm.reset();
    this.bonusMenuTrigger()?.closeMenu();
  }

  saveBonus() {
    if (!this.params?.data?.id || this.bonusForm.invalid) {
      return;
    }

    // Mark form as touched to show validation errors
    this.bonusForm.markAllAsTouched();

    if (this.bonusForm.invalid) {
      return;
    }

    this.isSaving.set(true);

    const bonusAmount = this.bonusForm.controls.amount.value;
    const bonusReason = this.bonusForm.controls.reason.value;

    this.payoutRequestService
      .updateExtraBonus(this.params.data.id, bonusAmount, bonusReason)
      .pipe(
        tap(() => {
          this.isSaving.set(false);
          this.cancelEdit();

          if (this.params?.data) {
            this.params.data.extra_bonus_in_eur = bonusAmount;
            this.params.data.extra_bonus_reason = bonusReason;

            this.params.data.calculatedFinalPayout = this.params.data.estimatedPayout + (bonusAmount ?? 0);

            this.params.api?.refreshCells({
              rowNodes: [this.params.node],
              force: true,
            });
          }
        }),
        this.errorHandlerService.catchHttpErrors(() => {
          this.isSaving.set(false);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private bonusValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const group = control as FormGroup<{ amount: FormControl<number>; reason: FormControl<string> }>;
    const amount = group.get('amount')?.value ?? 0;
    const reason = group.get('reason')?.value ?? '';

    if (amount > 0 && !reason.trim()) {
      return { reasonRequired: true };
    }

    return null;
  }
}
